import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type MouseEvent as ReactMouseEvent,
  type ChangeEvent,
} from 'react';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';

import { useWWSQueryContext } from '../context/WWSQueryContext';
import { themeFont } from '../lib/themeFont';
import { ActionButtonInput } from './ActionButton';
import GoogleFonts from './Layout/GoogleFonts';

import type { ThemeProps } from '@emotion/react';
import { PasswordPageSeo } from './Seo';

const Center = styled('div')`
  text-align: center;
  min-height: 100vh;
  padding-top: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props: ThemeProps): string => props.theme.colors.background};
  div {
    @media (max-width: 420px) {
      display: grid;
      max-width: 240px;
    }
  }
`;

const Title = styled('h4')`
  color: rgb(31, 31, 31);
  line-height: 1;
  margin: 24px 0 0;

  ${themeFont('s4')}
`;

const Fields = styled('div')`
  display: flex;
  justify-content: center;
`;

const LoginForm = styled('form')`
  margin: 24px auto;
  width: fit-content;
  text-align: center;
  display: flex;
  flex-direction: column;
`;

const SubmitButton = styled(ActionButtonInput)`
  width: fit-content;
  margin: 12px;
  @media (max-width: 420px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

const Input = styled('input')`
  color: black;
  padding: 12px;
  font-family: inherit;
  font-size: 100%;
  line-height: 1.15;
  margin: 12px;
  overflow: visible;
  border-radius: 2px;
  background: ${(props: ThemeProps): string => props.theme.colors.background};
  border: 1px solid ${(props: ThemeProps): string => props.theme.fonts.s6.color};

  ${themeFont('s6')}
  text-transform: none;

  ::placeholder {
    color: #000;

    ${themeFont('s6')}
  }

  ::-webkit-input-placeholder {
    color: #000;

    ${themeFont('s6')}
  }

  /* Firefox 19+ */
  ::-moz-placeholder {
    color: #000;

    ${themeFont('s6')}
  }

  /* Firefox 18- */
  :-moz-placeholder {
    color: #000;

    ${themeFont('s6')}
  }

  :-ms-input-placeholder {
    color: #000;

    ${themeFont('s6')}
  }
`;

interface ErrorProps {
  error: string | null;
}

const ErrorDiv = styled('div')`
  padding: 12px;
  margin: 12px;
  opacity: 0;
  transition: all 0.5s ease-out;
  background-color: ${(props: ThemeProps & ErrorProps) =>
    props.theme.colors.alert};

  ${(props: ThemeProps & ErrorProps): string =>
    props.error ? 'opacity: 1;' : ''}

  ${themeFont('s5')};
`;

function Login() {
  const [pin, setPin] = useState<string | undefined>('');

  const [error, setError] = useState<string | null>(null);
  const [hasAttempted, setHasAttempted] = useState<boolean>(false);
  const wasLoading = useRef<boolean>(false);

  const { fetchData: getLogin, loading, data } = useWWSQueryContext();

  const { query } = useRouter();

  const { slug } = query;

  const getCookieAttributes = useCallback((): string => {
    const isSecureContext =
      typeof window !== 'undefined' && window.location.protocol === 'https:';

    return `; Path=/; SameSite=Lax${isSecureContext ? '; Secure' : ''}`;
  }, []);

  useEffect((): void => {
    const hasCompletedRequest = wasLoading.current && !loading;

    if (hasCompletedRequest) {
      if (
        data?.weddingWebsiteResult?.__typename === 'WWS_Error' &&
        data.weddingWebsiteResult.cause === 'PIN_REQUIRED'
      ) {
        if (hasAttempted) {
          setError('Incorrect Password');
          document.cookie = `wws-pin=; Max-Age=0${getCookieAttributes()}`;
        }
      } else if (
        data?.weddingWebsiteResult?.__typename === 'WWS_WeddingWebsiteV2'
      ) {
        document.cookie = `wws-pin=${encodeURIComponent(
          typeof pin === 'string' ? pin : ''
        )}${getCookieAttributes()}`;
        // Reloading is kinda weird better would be to clear cache for error state
        window.location.reload();
      }
    }

    wasLoading.current = loading;
  }, [loading, data, pin, hasAttempted, getCookieAttributes]);

  const login = useCallback(
    async (
      evt: FormEvent<HTMLFormElement> | ReactMouseEvent<HTMLAnchorElement>
    ): Promise<null | undefined> => {
      evt.preventDefault();

      try {
        if (
          typeof slug !== 'undefined' &&
          !Array.isArray(slug) &&
          typeof pin !== 'undefined'
        ) {
          document.cookie = `wws-pin=${encodeURIComponent(pin)}${getCookieAttributes()}`;
          setHasAttempted(true);
          getLogin({
            variables: { slug, pin },
          });
        } else {
          throw new Error(`Login error: slug or pin are undefined`);
        }
      } catch {
        return null;
      }

      return;
    },
    [getLogin, getCookieAttributes, pin, slug]
  );

  const onPasswordChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>): void => {
      setError(null);

      setPin(evt.target.value);
    },
    []
  );

  return (
    <>
      <PasswordPageSeo>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
        />
      </PasswordPageSeo>

      <GoogleFonts />

      <Center>
        <div>
          <Title>Enter password to view this site</Title>

          <LoginForm onSubmit={login}>
            <Fields>
              <Input
                placeholder="Password"
                type="password"
                onChange={onPasswordChange}
                value={pin}
              />

              <SubmitButton type="submit" value="Submit" />
            </Fields>

            <ErrorDiv error={error}>{error}</ErrorDiv>
          </LoginForm>
        </div>
      </Center>
    </>
  );
}

export default memo(Login);
